// SU dial loader — v0M2.2.
//
// Loads dials from src/data/generated/dials.json (produced by
// scripts/convert-xlsx.ts against docs/Balance.xlsx). Values arrive as
// strings; callers use the typed getters below to coerce to number,
// split-mode {sp, mp}, or literal string as appropriate.
//
// SchemaVersion is asserted at load per SG-pattern discipline against
// silent version drift (§6.1 #7 pipeline lock).

import { assertSchemaVersion, BALANCE_SCHEMA_VERSION } from './loader';
import rawBlob from './generated/dials.json';

export interface DialEntry {
  readonly value: string;
  readonly notes: string;
}

export type Dials = Readonly<Record<string, DialEntry>>;

/**
 * Load the runtime dial dictionary. Fails loudly if the generated
 * JSON is missing the schemaVersion header or reports a version other
 * than the loader-expected one.
 */
export function loadDials(): Dials {
  assertSchemaVersion(rawBlob, BALANCE_SCHEMA_VERSION, 'src/data/generated/dials.json');
  return (rawBlob as unknown as { dials: Dials }).dials;
}

/** Retrieve a dial entry by name; throw on unknown names. */
export function getDial(dials: Dials, name: string): DialEntry {
  const entry = dials[name];
  if (!entry) {
    throw new Error(`Unknown dial: ${name}`);
  }
  return entry;
}

/**
 * Retrieve a dial as a finite number. Throws if the underlying value
 * fails Number() coercion or produces NaN / Infinity.
 */
export function getDialNumber(dials: Dials, name: string): number {
  const raw = getDial(dials, name).value;
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    throw new Error(`Dial ${name} is not a finite number: ${raw}`);
  }
  return n;
}

/**
 * Retrieve a split-mode dial "N <unit> SP / M <unit> MP" as {sp, mp}.
 * Used for dials whose value differs between single-player and
 * multiplayer (e.g. deployment_timer, round_pacing per BATTLES.md §2.7.8).
 */
export function getDialSplit(dials: Dials, name: string): { sp: number; mp: number } {
  const raw = getDial(dials, name).value;
  const match = raw.match(
    /(\d+(?:\.\d+)?)\s*[a-zA-Z]*\s*SP\s*\/\s*(\d+(?:\.\d+)?)\s*[a-zA-Z]*\s*MP/,
  );
  if (!match || match[1] === undefined || match[2] === undefined) {
    throw new Error(`Dial ${name} is not a split-mode value "N SP / M MP": ${raw}`);
  }
  return { sp: Number(match[1]), mp: Number(match[2]) };
}

/** Retrieve a dial as a literal string (no coercion). */
export function getDialString(dials: Dials, name: string): string {
  return getDial(dials, name).value;
}
